import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import CoordinatorWidgets from "@/components/CoordinatorWidgets";
import { TierBadge } from "@/pages/Leaderboard";
import { currency, fmtDate, relativeTime, PIPELINE_STAGES, PHASE_COLORS, countryFlag } from "@/lib/format";
import {
  TrendingUp, Users, Plane, DollarSign, Globe2, Handshake,
  Activity, Building2, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend,
} from "recharts";

const PIE_COLORS = ["#2A4D3E", "#D97757", "#B26B00", "#2980B9", "#7A5AC2", "#8E9992", "#5C6660", "#C0392B"];

export default function Dashboard({ role }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [patients, setPatients] = useState([]);
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const endpoint = role === "partner" ? "/analytics/partner" : "/analytics/admin";
        const [a, l, p] = await Promise.all([
          api.get(endpoint),
          api.get("/leads"),
          api.get("/patients"),
        ]);
        setData(a.data);
        setLeads(l.data);
        setPatients(p.data);
        if (role === "partner") {
          try { const t = await api.get("/partners/me/tier"); setTier(t.data); } catch {/* ignore */}
        }
      } catch (e) {
        setError(getApiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [role]);

  if (loading) return <div className="text-[#5C6660] text-sm">Loading dashboard…</div>;
  if (error) return <div className="text-[#C0392B] text-sm">{error}</div>;
  if (!data) return null;

  const h = data.headline || {};
  const isPartner = role === "partner";

  return (
    <div data-testid={`${role}-dashboard`}>
      <PageHeader
        eyebrow={`Welcome back, ${user?.name?.split(" ")[0] || ""}`}
        title={isPartner ? "Your referral performance" : "International patient operations"}
        subtitle={isPartner
          ? "Track your referrals, conversions and commission earnings."
          : "Track every international patient from first enquiry through follow-up."}
      />

      {isPartner && tier && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6 mb-6 relative overflow-hidden" data-testid="tier-card">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-10" style={{ background: tier.current.color }} />
          <div className="flex items-center justify-between gap-4 flex-wrap relative">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: tier.current.bg, color: tier.current.color }}>
                <span className="font-heading text-2xl font-bold">{tier.current.name[0]}</span>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#8E9992] font-semibold mb-0.5">Your current tier</div>
                <div className="font-heading text-3xl font-semibold text-[#2A332C] flex items-center gap-3">
                  {tier.current.name}
                  <TierBadge tier={tier.current} />
                </div>
                <div className="text-xs text-[#5C6660] mt-1">{(tier.current.commission_rate * 100).toFixed(0)}% commission · {tier.stats.conversions} conversions · {currency(tier.stats.revenue)} revenue</div>
              </div>
            </div>
            {tier.next && (
              <div className="min-w-[220px]">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-[#8E9992]">Progress to {tier.next.name}</span>
                  <span className="font-semibold text-[#2A332C]">{tier.progress_to_next}%</span>
                </div>
                <div className="h-2 bg-[#F2F4F3] rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${tier.progress_to_next}%`, background: tier.next.color }} />
                </div>
                <div className="text-[10px] text-[#8E9992] mt-1.5">
                  Need {tier.next.min_conversions} conversions or {currency(tier.next.min_revenue)} revenue
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {(role === "admin" || role === "coordinator") && <CoordinatorWidgets role={role} />}

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {isPartner ? (
          <>
            <StatCard label="My referrals" value={h.total_leads} sublabel={`${h.conversion_rate || 0}% conversion`} icon={Users} testid="stat-leads" />
            <StatCard label="Active patients" value={h.active_patients} sublabel="Currently in treatment" icon={Activity} accent="#D97757" testid="stat-active" />
            <StatCard label="Pending commission" value={currency(h.pending_commission)} sublabel="Awaiting payout" icon={DollarSign} accent="#B26B00" testid="stat-pending-commission" />
            <StatCard label="Total earned" value={currency(h.total_commission)} sublabel={`${currency(h.paid_commission)} paid`} icon={TrendingUp} accent="#2A4D3E" testid="stat-total-commission" />
          </>
        ) : (
          <>
            <StatCard label="Revenue (closed)" value={currency(h.total_revenue)} sublabel={`${currency(h.pipeline_revenue)} in pipeline`} icon={DollarSign} testid="stat-revenue" />
            <StatCard label="Active patients" value={h.active_patients} sublabel={`${h.total_patients} total`} icon={Plane} accent="#D97757" testid="stat-patients" />
            <StatCard label="Open leads" value={h.total_leads} sublabel={`${h.conversion_rate}% conversion`} icon={Users} accent="#B26B00" testid="stat-leads" />
            <StatCard label="Network" value={`${h.total_partners} · ${h.total_hospitals}`} sublabel="Partners · Hospitals" icon={Globe2} accent="#2980B9" testid="stat-network" />
          </>
        )}
      </div>

      {/* Pipeline funnel */}
      <Section title="Pipeline by stage" subtitle="Distribution of all journeys across the 17-stage IPMP pipeline">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white border border-[#E8E6E1] rounded-2xl p-6">
            <FunnelChart data={data.stage_funnel} />
          </div>
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
            <div className="text-sm font-medium text-[#2A332C] mb-4">Phase breakdown</div>
            <PhaseBreakdown stages={data.stage_funnel} />
          </div>
        </div>
      </Section>

      {!isPartner && (
        <>
          <Section title="Acquisition trend" subtitle="Last 6 months: leads received and revenue captured">
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.revenue_trend}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2A4D3E" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#2A4D3E" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="lds" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D97757" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#D97757" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="month" stroke="#8E9992" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#8E9992" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" stroke="#8E9992" fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue (USD)" stroke="#2A4D3E" strokeWidth={2} fill="url(#rev)" />
                  <Area yAxisId="right" type="monotone" dataKey="leads" name="New leads" stroke="#D97757" strokeWidth={2} fill="url(#lds)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
              <div className="font-heading font-semibold text-lg text-[#2A332C] mb-1">Source countries</div>
              <div className="text-xs text-[#8E9992] mb-4">Where international patients are coming from</div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={data.country_mix} dataKey="count" nameKey="country" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {data.country_mix.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
              <div className="font-heading font-semibold text-lg text-[#2A332C] mb-1">Top treatments</div>
              <div className="text-xs text-[#8E9992] mb-4">Most-requested procedures</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.treatment_mix} layout="vertical" margin={{ left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis type="number" stroke="#8E9992" fontSize={11} />
                  <YAxis type="category" dataKey="treatment" stroke="#5C6660" fontSize={11} width={120} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#D97757" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top partners + Hospital KPIs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid="top-partners">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-heading font-semibold text-lg text-[#2A332C]">Top referral partners</div>
                  <div className="text-xs text-[#8E9992]">By revenue contribution</div>
                </div>
                <Handshake size={18} className="text-[#D97757]" strokeWidth={1.6} />
              </div>
              <div className="space-y-3">
                {(data.top_partners || []).map((p, i) => (
                  <div key={p.partner_id} className="flex items-center justify-between py-2 border-b border-[#E8E6E1] last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#E6F0EB] text-[#2A4D3E] flex items-center justify-center text-xs font-semibold">{i + 1}</div>
                      <div>
                        <div className="text-sm font-medium text-[#2A332C]">{p.name}</div>
                        <div className="text-[11px] text-[#8E9992]">{p.organization} · {countryFlag(p.country)} {p.country}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-[#2A332C]">{currency(p.revenue)}</div>
                      <div className="text-[11px] text-[#8E9992]">{p.patients} pts · {currency(p.commission)} comm</div>
                    </div>
                  </div>
                ))}
                {(!data.top_partners || data.top_partners.length === 0) && (
                  <div className="text-sm text-[#8E9992] py-6 text-center">No partner activity yet.</div>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid="hospital-kpis">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-heading font-semibold text-lg text-[#2A332C]">Hospital performance</div>
                  <div className="text-xs text-[#8E9992]">Patient volume and revenue per provider</div>
                </div>
                <Building2 size={18} className="text-[#2A4D3E]" strokeWidth={1.6} />
              </div>
              <div className="space-y-3">
                {(data.hospital_kpis || []).map((h2) => (
                  <div key={h2.hospital_id} className="flex items-center justify-between py-2 border-b border-[#E8E6E1] last:border-0">
                    <div>
                      <div className="text-sm font-medium text-[#2A332C]">{h2.name}</div>
                      <div className="text-[11px] text-[#8E9992]">{h2.city}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-[#2A332C]">{currency(h2.revenue)}</div>
                      <div className="text-[11px] text-[#8E9992]">{h2.patients} patients</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Recent leads & patients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <RecentList title="Recent leads" link={`/${role}/leads`} testid="recent-leads">
          {leads.slice(0, 6).map((l) => (
            <Link key={l.id} to={`/${role}/leads/${l.id}`} className="flex items-center justify-between gap-3 py-3 border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2] -mx-2 px-2 rounded-lg transition">
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#2A332C] truncate">{countryFlag(l.country)} {l.patient_name}</div>
                <div className="text-[11px] text-[#8E9992] truncate">{l.treatment} · {relativeTime(l.created_at)}</div>
              </div>
              <StatusBadge kind="stage" value={l.stage} />
            </Link>
          ))}
          {leads.length === 0 && <div className="text-sm text-[#8E9992] py-6 text-center">No leads yet.</div>}
        </RecentList>
        <RecentList title="Active patients" link={`/${role}/patients`} testid="recent-patients">
          {patients.slice(0, 6).map((p) => (
            <Link key={p.id} to={`/${role}/patients/${p.id}`} className="flex items-center justify-between gap-3 py-3 border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2] -mx-2 px-2 rounded-lg transition">
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#2A332C] truncate">{countryFlag(p.country)} {p.name}</div>
                <div className="text-[11px] text-[#8E9992] truncate">{p.treatment} · {fmtDate(p.arrival_date || p.created_at)}</div>
              </div>
              <StatusBadge kind="stage" value={p.stage} />
            </Link>
          ))}
          {patients.length === 0 && <div className="text-sm text-[#8E9992] py-6 text-center">No patients yet.</div>}
        </RecentList>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mb-10">
      <div className="mb-4">
        <div className="font-heading text-xl font-semibold text-[#2A332C]">{title}</div>
        {subtitle && <div className="text-xs text-[#8E9992] mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function RecentList({ title, link, children, testid }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-semibold text-lg text-[#2A332C]">{title}</div>
        <Link to={link} className="text-xs text-[#2A4D3E] font-semibold hover:underline flex items-center gap-1">
          View all <ArrowUpRight size={13} />
        </Link>
      </div>
      <div>{children}</div>
    </div>
  );
}

function FunnelChart({ data }) {
  const chartData = data.map((s) => {
    const stage = PIPELINE_STAGES.find((x) => x.id === s.stage);
    return {
      ...s,
      label: stage?.label || s.stage,
      phase: stage?.phase || "Other",
    };
  });
  return (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart data={chartData} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
        <XAxis type="number" stroke="#8E9992" fontSize={11} />
        <YAxis type="category" dataKey="label" stroke="#5C6660" fontSize={11} width={160} />
        <Tooltip
          contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }}
          formatter={(value, _, ctx) => [`${value} leads`, ctx?.payload?.phase || ""]}
        />
        <Bar dataKey="count" radius={[0, 6, 6, 0]}>
          {chartData.map((d, i) => (
            <Cell key={i} fill={PHASE_COLORS[d.phase] || "#5C6660"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function PhaseBreakdown({ stages }) {
  const byPhase = {};
  stages.forEach((s) => {
    const stage = PIPELINE_STAGES.find((x) => x.id === s.stage);
    const phase = stage?.phase || "Other";
    byPhase[phase] = (byPhase[phase] || 0) + s.count;
  });
  const total = Object.values(byPhase).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="space-y-3">
      {Object.entries(byPhase).map(([phase, count]) => (
        <div key={phase}>
          <div className="flex items-center justify-between text-xs mb-1.5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: PHASE_COLORS[phase] }} />
              <span className="font-medium text-[#2A332C]">{phase}</span>
            </div>
            <span className="text-[#5C6660]">{count} · {Math.round((count / total) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-[#F2F4F3] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(count / total) * 100}%`, background: PHASE_COLORS[phase] }} />
          </div>
        </div>
      ))}
    </div>
  );
}
